import { useLocation } from 'preact-iso';
import { useLayoutEffect, useState } from 'preact/hooks';
import toast from 'react-hot-toast';
import scooterImageUrl from '../../assets/illustrations/scooter.svg';
import { Icon } from '../../components/icon';
import { Link } from '../../components/link';
import {
  ContinueWithAppleButton,
  ContinueWithGoogleButton,
} from '../../components/sign-in-buttons';
import TopNav from '../../components/top-nav';
import { useUser, useUserActions } from '../../global-state/user';
import { usePageTitle } from '../../utils/page-title';
import useMediaQuery from '../../utils/use-media-query';
import style from './style.module.scss';

const SignIn = () => {
  usePageTitle('Sign In');
  const user = useUser();
  const { signInWithPassword, resetPassword } = useUserActions();
  const showAside = useMediaQuery('(min-width: 900px)');
  const [isSubmitting, setSubmitting] = useState(false);
  const { route } = useLocation();

  useLayoutEffect(() => {
    if (user) {
      console.log('Welcome back! You are already signed in.');
      route('/now');
    }
  }, [user]);

  const onEmailPassSubmit = (event: Event) => {
    event.preventDefault();
    const form = event.target as HTMLFormElement;
    const data = new FormData(form);
    const email = (data.get('email') as string).trim();
    const password = data.get('password') as string;

    setSubmitting(true);
    signInWithPassword({ email, password }).then(() => {
      setSubmitting(false);
    });
  };

  const onResetPasswordClick = (event: Event) => {
    event.preventDefault();
    const emailInput = document.querySelector(
      'input[name="email"]'
    ) as HTMLInputElement;

    const email = emailInput.value.trim();
    if (!email || !emailInput.checkValidity()) {
      emailInput.focus();
      toast.error('Please enter a valid email address');
      return;
    }

    resetPassword(emailInput.value.trim());
  };

  return (
    <div class={style.wrapper}>
      <TopNav />
      <div class="top-section">
        <h1 class={style.heading}>Sign In</h1>

        <div className="flex flex-wrap gap-2">
          <ContinueWithGoogleButton />
          <ContinueWithAppleButton />
        </div>

        <p style="text-align: center; margin: 0.5rem 0 0.75rem"> - or -</p>

        {/* @ts-ignore */}
        <form class={style.form} onSubmit={onEmailPassSubmit}>
          <label>
            <span>Email Address</span>
            <input name="email" type="email" autoComplete="email" required />
          </label>
          <label>
            <span>Password</span>
            <input
              name="password"
              type="password"
              autoComplete="current-password"
              minLength={12}
              required
            />
          </label>
          <button class="button button--submit" type="submit" disabled={isSubmitting}>
            Sign In
            {isSubmitting && <Icon sprite="loading-circle" />}
          </button>
          <p>
            Forgot your password?{' '}
            <a href="#" onClick={onResetPasswordClick} class="text-blue-500 underline">
              Send password reset email
            </a>
            .
          </p>
        </form>

        <p style={{ marginTop: '1.5rem' }}>
          New to Adblock Podcast?{' '}
          <Link href="/sign-up" class="text-blue-500 underline">
            Create an account
          </Link>
        </p>

        <p class="mt-3" style="font-size: 16px">
          <small>
            By signing in and using this app you acknowledge and agree to abide by our{' '}
            <Link href="/terms-of-use" target="_blank" class="text-blue-500 underline">
              Terms&nbsp;of&nbsp;Use
            </Link>{' '}
            and{' '}
            <Link
              href="/privacy-policy"
              target="_blank"
              class="text-blue-500 underline"
            >
              Privacy&nbsp;Policy
            </Link>
            .
          </small>
        </p>
      </div>

      {showAside ? (
        <aside
          class={style.quoteAndImage}
          style={{ backgroundColor: '#728EDE', color: '#30242A' }}
        >
          <blockquote>
            <p>
              There is the great lesson of 'Beauty and the Beast,' that a thing must be
              loved before it is lovable.
            </p>
            <div>- G.K. Chesterton</div>
          </blockquote>
          <img src={scooterImageUrl} alt="" aria-hidden="true" />
        </aside>
      ) : (
        ''
      )}
    </div>
  );
};

export default SignIn;
